/**
 * DecisionFlow — ManagerShell
 * ui/src/shells/ManagerShell.jsx
 * S75 — C6 backend contract complete
 *
 * Shell for user_type = "manager". Route root: /manager/
 *
 * Three panels (spec v1.3 §3.3, §5.3):
 *   /manager/team-payout   — TeamPayout
 *   /manager/attainment    — TeamAttainment
 *   /manager/statements    — TeamStatements
 *
 * Architecture rules:
 *   DD-04: No cross-shell imports. Shared/ only.
 *   DD-05: Role always from JWT user_type. No dev role switcher (spec §4.3).
 *   DD-06: Every value from live API. No hardcoded display values in prod.
 *
 * API endpoints (api_contract v1.10, manager_service.py):
 *   GET /api/v1/manager/team-payout?fiscal_period_id={id}
 *     Response: { manager_position_id, fiscal_period_id, period_code,
 *                 team_summary: { total_positions, total_payout,
 *                                 average_attainment_pct, currency_code },
 *                 positions: [{ position_id, full_name, total_payout,
 *                               attainment_pct, payout_summary_id }] }
 *   GET /api/v1/manager/team-attainment?fiscal_period_id={id}
 *     Response: { manager_position_id, fiscal_period_id, total_positions,
 *                 positions_above_100pct, positions_at_gate,
 *                 average_attainment_pct, total_payout }
 *   GET /api/v1/statement-batches
 *     Manager sees released batches only — server-enforced.
 *   GET /api/v1/fiscal-periods?period_status=open
 *     Active period — fiscal_period_id required for team-payout and team-attainment.
 *   GET /api/v1/payout-summaries/{payout_summary_id}
 *     Drill-to-detail — manager hierarchy check enforced server-side (C6-T-13).
 *     Called when manager clicks a rep row in TeamPayout.
 *     Response: full payout_summary + components[] breakdown.
 *
 * RBAC: manager only — server enforces on all endpoints.
 * Sensitive read log: read.team_payout — server fires on manager access.
 * Scope: server-enforced hierarchy via recursive CTE — no client filtering.
 *
 * ContextHeader (spec §6.4):
 *   Page title (H1), team scope label, fiscal period, no progress bars.
 *
 * Copilot: inline, Standard+ tier only (spec §7).
 *   ER-1: no client-side tier evaluation. Attempt API call on submit.
 *   Handle 403 TIER_FEATURE_UNAVAILABLE → tierBlocked state → suppress input.
 */

import { useState, useEffect } from "react";
import { Routes, Route, NavLink, Navigate, useLocation, Link } from "react-router-dom";
import {
  Users, Target, FileText, TrendingUp,
  AlertTriangle, CheckCircle2, ChevronRight, X, DollarSign, Award, BarChart2,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext.jsx";
import Button from "../components/shared/Button.jsx";
import Card from "../components/shared/Card.jsx";
import CopilotPanel from "../components/shared/CopilotPanel.jsx";
import Field from "../components/shared/Field.jsx";
import Pill from "../components/shared/Pill.jsx";
import TopBar from "../components/shared/TopBar.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Shared fetch hook — cookie auth only (no Bearer token, S160 auth fix)
// ---------------------------------------------------------------------------

function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError(null);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

function fmt(val, currency = "USD") {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-US", { style: "currency", currency });
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${(Number(val) * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// usePayoutDetail — fetch payout summary by id or by position_id query
// ---------------------------------------------------------------------------

function usePayoutDetail({ payoutSummaryId, positionId, fiscalPeriodId }) {
  const direct = useFetch(
    payoutSummaryId ? `${API_BASE}/payout-summaries/${payoutSummaryId}` : null
  );
  const query = useFetch(
    !payoutSummaryId && positionId && fiscalPeriodId
      ? `${API_BASE}/payout-summaries?position_id=${positionId}&fiscal_period_id=${fiscalPeriodId}&is_latest_final=true`
      : null
  );
  if (payoutSummaryId) return direct;
  // List endpoint response: quota (not quota_reporting), credit_total (not credit_total_reporting)
  // Synthesise a components[0] so DetailSidePanel renders correctly
  const listRow = query.data?.data?.[0] ?? null;
  const syntheticData = listRow ? {
    ...listRow,
    total_payout: listRow.total_payout,
    components: [{
      component_type: "ARR",
      quota: listRow.quota,
      credit: listRow.credit_total,
      attainment_pct: listRow.attainment_pct,
      component_payout: listRow.arr_payout,
    }],
    calculation_trace: null,
  } : null;
  return {
    data: syntheticData,
    loading: query.loading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// DetailSidePanel
// Fixed right-side slide-in panel showing full payout breakdown.
// selection: { payout_summary_id?, position_id?, full_name, calculation_mode }
// All three panels (TeamPayout, TeamAttainment, TeamStatements) share this.
// ---------------------------------------------------------------------------

function DetailSidePanel({ selection, fiscalPeriodId, onClose }) {
  const { data, loading, error } = usePayoutDetail({
    payoutSummaryId: selection?.payout_summary_id ?? null,
    positionId: selection?.position_id ?? null,
    fiscalPeriodId,
  });

  const isOpen = !!selection;

  return (
    <>

      <div className={`fixed top-0 right-0 z-40 h-full w-80 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-2 mb-0.5">
              <BarChart2 className="h-4 w-4 text-[rgb(var(--color-text-muted))] shrink-0" />
              <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Payout Detail</p>
            </div>
            <p className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">
              {selection?.full_name ?? "—"}
            </p>
            {selection?.calculation_mode && (
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {selection.calculation_mode === "ic" ? "Individual Contributor" :
                 selection.calculation_mode === "rollup" ? "Rollup Manager" :
                 selection.calculation_mode === "hybrid" ? "Hybrid Manager" :
                 selection.calculation_mode === "quota_exempt" ? "Quota Exempt" :
                 selection.calculation_mode}
              </p>
            )}
          </div>
          <button onClick={onClose} className="shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] transition" aria-label="Close panel">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <LoadingRow />}
          {error && (
            <ErrorNote message={error === "403" ? "You do not have access to this position's payout detail." : "Unable to load payout detail. Please try again."} />
          )}
          {data && (
            <>
              {/* Top-line summary tiles
                  GET /payout-summaries/{id} response shape (payout_summaries_router.py):
                  top-level: total_payout, currency_code, position_name
                  components[0]: quota, credit, attainment_pct, component_payout
                  calculation_trace: ote_midpoint, rate_band_applied, manual_proof
              */}
              {(() => {
                const c0 = data.components?.[0];
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Total Payout</p>
                      <p className="text-tok-lg font-bold text-[rgb(var(--color-text))]">{fmt(data.total_payout)}</p>
                    </div>
                    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Attainment</p>
                      <p className="text-tok-lg font-bold text-[rgb(var(--color-text))]">{fmtPct(c0?.attainment_pct)}</p>
                    </div>
                    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Quota</p>
                      <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmt(c0?.quota)}</p>
                    </div>
                    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Credit</p>
                      <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmt(c0?.credit)}</p>
                    </div>
                    {data.calculation_trace?.ote_midpoint != null && (
                      <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">OTE</p>
                        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmt(data.calculation_trace.ote_midpoint)}</p>
                      </div>
                    )}
                    {data.calculation_trace?.rate_band_applied != null && (() => {
                      // OI-RATE-BAND-BOUNDARIES (S245): rate_band_applied is
                      // { from_pct, to_pct, payout_pct } — not a boolean.
                      // Show band range when from_pct/to_pct populated (new runs).
                      // Fall back to "Yes" for old runs (nulls), "No" if no band.
                      const band = data.calculation_trace.rate_band_applied;
                      const hasBand = !!band;
                      const hasRange = hasBand && band.from_pct != null && band.to_pct != null;
                      const bandLabel = !hasBand
                        ? "No"
                        : hasRange
                        ? `${(Number(band.from_pct) * 100).toFixed(0)}% – ${(Number(band.to_pct) * 100).toFixed(0)}%`
                        : "Yes";
                      return (
                        <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-3">
                          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Rate Band</p>
                          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{bandLabel}</p>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Component breakdown */}
              {data.components?.length > 0 && (
                <div>
                  <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Component Breakdown</p>
                  <div className="space-y-2">
                    {data.components.map((c, i) => (
                      <div key={c.payout_detail_id ?? i} className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{c.component_type}</span>
                            {c.ramp_factor && Number(c.ramp_factor) < 1 && (
                              <Pill tone="Draft">Ramped {fmtPct(c.ramp_factor)}</Pill>
                            )}
                          </div>
                          {/* §15.2 S578: Held badge when gate_met=FALSE (exception blocking gate fired) */}
                          {c.gate_met === false && c.source_ref_id?.startsWith("blocked_by_exception:") ? (
                            <div className="flex items-center gap-1.5">
                              <span className="text-tok-sm font-bold text-[rgb(var(--color-text))]">$0.00</span>
                              <Pill tone="Warning">
                                <Link
                                  to={`/exceptions/${c.source_ref_id.replace("blocked_by_exception:", "")}`}
                                  className="flex items-center gap-1"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  Held — Exception #{c.source_ref_id.replace("blocked_by_exception:", "").slice(0, 8)}
                                </Link>
                              </Pill>
                            </div>
                          ) : (
                            <span className="text-tok-sm font-bold text-[rgb(var(--color-text))]">{fmt(c.component_payout)}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          {c.component_type === "ARR" && (<>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Quota</span>{fmt(c.quota)}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Credit</span>{fmt(c.credit)}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Attainment</span>{fmtPct(c.attainment_pct)}</div>
                          </>)}
                          {c.component_type === "MBO" && (<>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Code</span>{c.mbo_code ?? "—"}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Scoring</span>{c.scoring_method ?? "—"}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Achieved</span>{c.achieved_flag === true ? "Yes" : c.achieved_flag === false ? "No" : c.achievement_pct ?? "—"}</div>
                          </>)}
                          {c.component_type === "NPS" && (<>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">NPS Score</span>{c.nps_score ?? "—"}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Tier</span>{c.tier_sequence ?? "—"}</div>
                            <div><span className="block text-[rgb(var(--color-text-muted))]">Factor</span>{c.payout_factor != null ? `${(Number(c.payout_factor) * 100).toFixed(0)}%` : "—"}</div>
                          </>)}
                          {c.component_type !== "ARR" && c.component_type !== "MBO" && c.component_type !== "NPS" && (<>
                            <div className="col-span-3 text-[rgb(var(--color-text-muted))] italic">No breakdown available</div>
                          </>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Calculation trace */}
              {data.calculation_trace && (
                <div>
                  <p className="mb-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">How It Was Calculated</p>
                  <div className="space-y-2">
                    {/* OTE context */}
                    {data.calculation_trace.ote_midpoint != null && (
                      <div className="flex justify-between text-tok-sm pb-2 border-b border-[rgb(var(--color-border))]">
                        <span className="text-[rgb(var(--color-text-muted))]">OTE Midpoint</span>
                        <span className="font-medium text-[rgb(var(--color-text))]">{fmt(data.calculation_trace.ote_midpoint)}</span>
                      </div>
                    )}
                    {/* Structured proof steps (new — proof_steps from backend) */}
                    {data.calculation_trace.proof_steps?.length > 0 ? (() => {
                      // Group steps by section (ARR / MBO / NPS / TOTAL) preserving order
                      const sections = [];
                      let current = null;
                      for (const step of data.calculation_trace.proof_steps) {
                        const sec = step.section ?? "ARR";
                        if (!current || current.name !== sec) {
                          current = { name: sec, steps: [] };
                          sections.push(current);
                        }
                        current.steps.push(step);
                      }
                      return (
                        <div className="space-y-2">
                          {sections.map((sec) => {
                            const isTotal = sec.name === "TOTAL";
                            return (
                              <div key={sec.name} className={`rounded-xl overflow-hidden ${isTotal ? "border-2 border-[rgb(var(--color-border-strong))]" : "border border-[rgb(var(--color-border))]"}`}>
                                {!isTotal && sections.filter(s => s.name !== "TOTAL").length > 1 && (
                                  <div className="px-3 py-1.5 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
                                    <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{sec.name}</span>
                                  </div>
                                )}
                                {sec.steps.map((step, i) => (
                                  <div
                                    key={i}
                                    className={`flex items-start justify-between px-3 py-2.5 text-tok-sm ${
                                      isTotal
                                        ? "bg-[rgb(var(--color-primary))] text-white"
                                        : step.is_result
                                          ? "bg-[rgb(var(--color-surface-muted))]"
                                          : i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"
                                    }`}
                                  >
                                    <div>
                                      <span className={`font-medium ${
                                        isTotal
                                          ? "text-white"
                                          : step.is_result
                                            ? "text-white"
                                            : "text-[rgb(var(--color-text-muted))]"
                                      }`}>
                                        {step.label}
                                      </span>
                                      {step.note && (
                                        <p className={`text-tok-xs mt-0.5 ${
                                          isTotal
                                            ? "text-[rgb(var(--color-text-muted))]"
                                            : step.is_result
                                              ? "text-[rgb(var(--color-text-muted))]"
                                              : "text-[rgb(var(--color-text-muted))]"
                                        }`}>
                                          {step.note}
                                        </p>
                                      )}
                                    </div>
                                    <span className={`font-bold ml-4 shrink-0 ${
                                      isTotal
                                        ? "text-white text-tok-base"
                                        : step.is_result
                                          ? "text-white text-tok-base"
                                          : "text-[rgb(var(--color-text))]"
                                    }`}>
                                      {step.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })() : data.calculation_trace.manual_proof ? (
                      /* Fallback: monospace proof string for older data */
                      <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                        <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">{data.calculation_trace.manual_proof}</p>
                      </div>
                    ) : null}
                    {data.calculation_trace.interpolation != null && (
                      <div className="flex justify-between text-tok-sm pt-1">
                        <span className="text-[rgb(var(--color-text-muted))]">Interpolation</span>
                        <span className="font-medium text-[rgb(var(--color-text))]">{data.calculation_trace.interpolation ? "Yes" : "No"}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// Panel: MyPayout
// GET /api/v1/manager/my-payout?fiscal_period_id={id}
// S616: Acknowledgment gate — manager must acknowledge own plan before
// seeing own payout figures. Team views are NOT gated (Model B-Prime:
// one person's non-acknowledgment has zero effect on others).
// GET /api/v1/my/plan-acknowledgment RBAC extended to include manager (S616).
// ---------------------------------------------------------------------------

function useMyAcknowledgment(fiscalPeriodId, refreshKey = 0) {
  const [acknowledged, setAcknowledged] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fiscalPeriodId) { setLoading(false); return; }
    setLoading(true);
    fetch(
      `${API_BASE}/my/plan-acknowledgment?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setAcknowledged(data?.acknowledged === true))
      .catch(() => setAcknowledged(false))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, refreshKey]);

  return { acknowledged, loading };
}

// AcknowledgmentFlow — S616
// Shown when manager has not yet acknowledged their plan.
// Fetches plan terms, presents key plan details, then allows one-click acknowledge.
// On success: parent refreshes acknowledged state → payout figures reveal.
function AcknowledgmentFlow({ fpId, onAcknowledged }) {
  const { data: termsData, loading: termsLoading, error: termsError } = useFetch(
    fpId ? `${API_BASE}/my/plan-terms?fiscal_period_id=${fpId}` : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [ackError,   setAckError]   = useState(null);

  const terms = termsData?.plan_terms ?? null;
  const pc    = terms?.plan_component ?? null;

  async function handleAcknowledge() {
    if (!terms?.plan_assignment_id) return;
    setSubmitting(true);
    setAckError(null);
    try {
      const res = await fetch(`${API_BASE}/my/plan-acknowledgment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_assignment_id:    terms.plan_assignment_id,
          fiscal_period_id:      fpId,
          acknowledgment_method: "in_app",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "ACK_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      onAcknowledged();
    } catch (e) {
      setAckError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (termsLoading) return <LoadingRow />;
  if (termsError || !terms) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
          <span className="mt-0.5 shrink-0">⚠</span>
          <span>No active plan assignment found for this period. Contact your comp admin.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Plan summary — show before acknowledging so manager knows what they're signing */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 space-y-3">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Your Compensation Plan
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-tok-sm">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.plan_code ?? "—"}</p>
          </div>
          {pc?.ote_midpoint && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">OTE</p>
              <p className="font-medium text-[rgb(var(--color-text))]">
                {Number(pc.ote_midpoint).toLocaleString("en-US", { style: "currency", currency: pc.ote_currency_code ?? "USD" })}
              </p>
            </div>
          )}
          {pc?.variable_pct && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable</p>
              <p className="font-medium text-[rgb(var(--color-text))]">
                {`${(Number(pc.variable_pct) * 100).toFixed(0)}%`}
              </p>
            </div>
          )}
          {pc?.arr_weight && Number(pc.arr_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">ARR Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.arr_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          {pc?.mbo_weight && Number(pc.mbo_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">MBO Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.mbo_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.effective_date ?? "—"}</p>
          </div>
        </div>
      </div>

      {/* Acknowledge CTA */}
      <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <span className="mt-0.5 shrink-0 text-[rgb(var(--color-warning))]">⚠</span>
        <div className="flex-1 space-y-2">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Acknowledge your plan to view your earnings.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            By acknowledging, you confirm you have reviewed and understood your compensation plan for this period.
          </p>
          {ackError && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">{ackError}</p>
          )}
          <button
            onClick={handleAcknowledge}
            disabled={submitting}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Acknowledging…" : "Acknowledge My Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyPayout({ period, profile }) {
  const fpId = period?.fiscal_period_id;
  const isRollup = profile?.calculation_mode === "rollup";

  const { data, loading, error } = useFetch(
    fpId ? `${API_BASE}/manager/my-payout?fiscal_period_id=${fpId}` : null
  );
  const [ackRefreshKey, setAckRefreshKey] = useState(0);
  const { acknowledged, loading: ackLoading } = useMyAcknowledgment(
    // Rollup managers have no plan to acknowledge — skip the check entirely
    !isRollup && fpId ? fpId : null,
    ackRefreshKey,
  );

  // Rollup: acknowledged is null (skipped) — treat as gated=false so data shows freely
  const gated = !isRollup && !ackLoading && acknowledged === false;

  return (
    <div className="space-y-4">
      <Card title="My Payout">
        {!fpId && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period found.</p>}
        {fpId && loading && <LoadingRow />}
        {fpId && error && (
          error === "404"
            ? <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No payout data available for this period.</p>
            : <ErrorNote message="Unable to load your payout data." />
        )}

        {/* Hybrid: show acknowledgment flow until acknowledged */}
        {fpId && !loading && !error && !isRollup && !ackLoading && gated && (
          <AcknowledgmentFlow fpId={fpId} onAcknowledged={() => setAckRefreshKey((k) => k + 1)} />
        )}

        {/* Show payout data when: rollup (always) or hybrid (after acknowledging) */}
        {data && (isRollup || acknowledged) && (
          <>
            {profile?.calculation_mode && (
              <div className="mb-3">
                <span className="inline-flex items-center rounded-full bg-[rgb(var(--color-surface-muted))] px-2.5 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  {profile.calculation_mode === "rollup" ? "Rollup Manager" : "Hybrid Manager"}
                </span>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Total Payout"     value={fmt(data.total_payout)} />
              <Field label="Attainment"       value={fmtPct(data.attainment_pct)} />
              {data.quota_reporting != null && <Field label="Quota"  value={fmt(data.quota_reporting)} />}
              {data.credit_total_reporting != null && <Field label="Credit" value={fmt(data.credit_total_reporting)} />}
              <Field label="ARR Payout"       value={fmt(data.arr_payout)} />
              {Number(data.mbo_payout) > 0 && <Field label="MBO Payout"  value={fmt(data.mbo_payout)} />}
              {Number(data.nps_payout) > 0 && <Field label="NPS Payout"  value={fmt(data.nps_payout)} />}
              {Number(data.adoption_payout) > 0 && <Field label="Adoption Payout" value={fmt(data.adoption_payout)} />}
              {data.payout_as_pct_ote != null && <Field label="% of OTE" value={fmtPct(data.payout_as_pct_ote)} />}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: MyAttainment
// GET /api/v1/manager/my-attainment?fiscal_period_id={id}
// attainment_label is server-derived — never hardcoded client-side (DR-004).
// ---------------------------------------------------------------------------

function MyAttainment({ period, profile }) {
  const fpId = period?.fiscal_period_id;
  const { data, loading, error } = useFetch(
    fpId ? `${API_BASE}/manager/my-attainment?fiscal_period_id=${fpId}` : null
  );

  const attainmentLabel = data?.attainment_label ?? "My Attainment";

  return (
    <div className="space-y-4">
      <Card title={attainmentLabel}>
        {!fpId && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period found.</p>}
        {fpId && loading && <LoadingRow />}
        {fpId && error && <ErrorNote message="Unable to load your attainment data." />}
        {data && (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-xl bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgb(var(--color-border))]">
                <Award className="h-5 w-5 text-[rgb(var(--color-text-muted))]" />
              </div>
              <div>
                <p className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">
                  {fmtPct(data.attainment_pct)}
                </p>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{attainmentLabel}</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Quota"  value={fmt(data.quota_reporting)} />
              <Field label="Credit" value={fmt(data.credit_total_reporting)} />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// Depth → left padding (depth=1 no indent, depth=2 one level, depth=3 two levels)
const DEPTH_INDENT = { 1: "pl-0", 2: "pl-6", 3: "pl-12" };

function calcModeLabel(mode) {
  if (mode === "rollup")       return { label: "Rollup",  cls: "bg-[rgb(var(--color-info)/15)] text-[rgb(var(--color-info))]" };
  if (mode === "hybrid")       return { label: "Hybrid",  cls: "bg-[rgb(var(--color-primary)/15)] text-[rgb(var(--color-primary))]" };
  if (mode === "ic")           return { label: "IC",      cls: "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]" };
  if (mode === "quota_exempt") return { label: "Exempt",  cls: "bg-[rgb(var(--color-warning)/15)] text-[rgb(var(--color-warning))]" };
  return null;
}

// ---------------------------------------------------------------------------
// Panel: TeamPayout (merged — S616)
// Calls team-payout + team-attainment, joins on position_id.
// Columns: Name | Quota | Credit | Attainment | Payout
// Acknowledgment shown as subtle dot on name — data always visible (Model B-Prime).
// Footer: total quota, total credit, team attainment, total payout.
// Side panel detail preserved — clicking any row opens DetailSidePanel.
// ---------------------------------------------------------------------------

function TeamPayout({ period, selectedPosition, onSelectPosition }) {
  const fpId = period?.fiscal_period_id;

  const { data: payoutData, loading: payoutLoading, error: payoutError } = useFetch(
    fpId ? `${API_BASE}/manager/team-payout?fiscal_period_id=${fpId}` : null
  );
  const { data: attainData, loading: attainLoading } = useFetch(
    fpId ? `${API_BASE}/manager/team-attainment?fiscal_period_id=${fpId}` : null
  );
  const { data: ackData } = useFetch(
    fpId ? `${API_BASE}/plan-acknowledgments?fiscal_period_id=${fpId}&limit=500` : null
  );

  const loading = payoutLoading || attainLoading;

  // Build acknowledgment lookup: position_id → acknowledged bool
  const ackMap = {};
  for (const r of (ackData?.data ?? [])) {
    ackMap[r.position_id] = r.acknowledged;
  }

  // Build attainment lookup: position_id → { quota_reporting, credit_total_reporting, attainment_pct }
  const attainMap = {};
  for (const p of (attainData?.positions ?? [])) {
    attainMap[p.position_id] = p;
  }

  const summary   = payoutData?.team_summary ?? null;
  const attainSum = attainData ?? null;
  const positions = payoutData?.positions ?? [];

  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  function handleRowClick(p) {
    if (selectedPosition?.position_id === p.position_id) {
      onSelectPosition(null);
    } else {
      onSelectPosition({ payout_summary_id: p.payout_summary_id, position_id: p.position_id, full_name: p.full_name, calculation_mode: p.calculation_mode });
    }
  }

  function toggleNode(positionId) {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) { next.delete(positionId); } else { next.add(positionId); }
      return next;
    });
  }

  function getHiddenIds(positions, collapsed) {
    const hidden = new Set();
    let suppressFromDepth = null;
    for (const p of positions) {
      if (suppressFromDepth !== null) {
        if (p.depth > suppressFromDepth) { hidden.add(p.position_id); continue; }
        else { suppressFromDepth = null; }
      }
      if (collapsed.has(p.position_id) && p.has_direct_reports) {
        suppressFromDepth = p.depth;
      }
    }
    return hidden;
  }

  const hiddenIds = getHiddenIds(positions, collapsedNodes);
  const visiblePositions = positions.filter((p) => !hiddenIds.has(p.position_id));
  const unacknowledgedCount = Object.values(ackMap).filter((v) => v === false).length;

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <Card title="Team Payout Summary">
        {!fpId && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period found.</p>}
        {fpId && loading && <LoadingRow />}
        {fpId && payoutError && <ErrorNote message="Unable to load team payout data." />}
        {summary && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Total Positions" value={summary.total_positions ?? "—"} />
            <Field label="Total Payout"    value={fmt(summary.total_payout, summary.currency_code ?? "USD")} />
            <Field label="Avg Attainment"  value={fmtPct(attainSum?.average_attainment_pct)} />
            <Field label="Currency"        value={summary.currency_code ?? "—"} />
          </div>
        )}
      </Card>

      {/* Merged breakdown table */}
      <Card title="Team Breakdown">
        {fpId && loading && <LoadingRow />}
        {fpId && payoutError && <ErrorNote message="Unable to load team breakdown." />}
        {!loading && !payoutError && positions.length === 0 && fpId && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No finalised payout records for this period.</p>
        )}
        {positions.length > 0 && (
          <>
            <div className="mt-2 max-h-[520px] overflow-y-auto space-y-1">
              {/* Header */}
              <div className="grid grid-cols-6 gap-2 px-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                <span className="col-span-2">Name</span>
                <span className="text-right">Quota</span>
                <span className="text-right">Credit</span>
                <span className="text-right">Attainment</span>
                <span className="text-right">Payout</span>
              </div>

              {visiblePositions.map((p) => {
                const isSelected  = selectedPosition?.position_id === p.position_id;
                const isCollapsed = collapsedNodes.has(p.position_id);
                const indentClass = DEPTH_INDENT[p.depth] ?? "pl-12";
                const modeTag     = calcModeLabel(p.calculation_mode);
                const isNode      = p.has_direct_reports;
                const attn        = attainMap[p.position_id] ?? null;
                const isAcked     = ackMap[p.position_id];  // true | false | undefined

                return (
                  <div key={p.position_id} className={indentClass}>
                    <div className={`rounded-xl border transition ${
                      isSelected
                        ? "border-[rgb(var(--color-border-strong))] bg-[rgb(var(--color-surface-muted))]"
                        : "border-[rgb(var(--color-border))]"
                    }`}>
                      <div className="flex items-center gap-1">
                        {isNode ? (
                          <button
                            onClick={() => toggleNode(p.position_id)}
                            className="shrink-0 pl-2 py-2.5 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                            aria-label={isCollapsed ? "Expand" : "Collapse"}
                          >
                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                          </button>
                        ) : (
                          <div className="w-6 shrink-0" />
                        )}
                        <button
                          onClick={() => handleRowClick(p)}
                          className="flex-1 grid grid-cols-6 gap-2 py-2.5 pr-3 text-tok-sm text-left"
                          aria-expanded={isSelected}
                        >
                          <span className="col-span-2 flex items-center gap-1.5 min-w-0">
                            {/* Subtle ack indicator — amber dot if not acknowledged, no dot if acknowledged */}
                            {isAcked === false && (
                              <span
                                className="h-3 w-3 shrink-0 rounded-full bg-[rgb(var(--color-warning))]"
                                title="Plan not yet acknowledged"
                              />
                            )}
                            <span className="font-medium truncate text-[rgb(var(--color-text))]">
                              {p.full_name}
                            </span>
                            {modeTag && (
                              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-tok-xs font-medium ${modeTag.cls}`}>
                                {modeTag.label}
                              </span>
                            )}
                          </span>
                          <span className="text-right text-[rgb(var(--color-text-muted))]">
                            {fmt(attn?.quota_reporting)}
                          </span>
                          <span className="text-right text-[rgb(var(--color-text-muted))]">
                            {fmt(attn?.credit_total_reporting)}
                          </span>
                          <span className="text-right">
                            <Pill tone={
                              Number(attn?.attainment_pct) >= 1   ? "Complete" :
                              Number(attn?.attainment_pct) >= 0.8 ? "Ready"    : "Draft"
                            }>
                              {fmtPct(attn?.attainment_pct)}
                            </Pill>
                          </span>
                          <span className="text-right font-medium text-[rgb(var(--color-text))]">
                            {fmt(p.total_payout)}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer totals */}
            {attainSum && (
              <div className="mt-3 grid grid-cols-6 gap-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5 text-tok-xs">
                <span className="col-span-2 font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Team Total</span>
                <span className="text-right font-semibold text-[rgb(var(--color-text))]">
                  {attainSum.team_quota != null ? fmt(attainSum.team_quota) : "—"}
                </span>
                <span className="text-right font-semibold text-[rgb(var(--color-text))]">
                  {attainSum.team_credit != null ? fmt(attainSum.team_credit) : "—"}
                </span>
                <span className="text-right font-semibold text-[rgb(var(--color-text))]">
                  {fmtPct(attainSum.average_attainment_pct)}
                </span>
                <span className="text-right font-semibold text-[rgb(var(--color-text))]">
                  {summary ? fmt(summary.total_payout, summary.currency_code ?? "USD") : "—"}
                </span>
              </div>
            )}

            {/* Acknowledgment note — informational only, subtle */}
            {unacknowledgedCount > 0 && (
              <p className="mt-2 flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                <span className="h-3 w-3 rounded-full bg-[rgb(var(--color-warning))] shrink-0" />
                {unacknowledgedCount} rep{unacknowledgedCount !== 1 ? "s have" : " has"} not yet acknowledged their plan — payout figures are hidden from them until they do.
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Panel: TeamAttainment
// GET /api/v1/manager/team-attainment?fiscal_period_id={id}
// S163 §15B: hierarchical position table with quota, credit, attainment.
// Clicking a row opens PayoutDetailDrawer (same as TeamPayout).
// ---------------------------------------------------------------------------

function TeamAttainment({ period, selectedPosition, onSelectPosition }) {
  const fpId = period?.fiscal_period_id;
  const { data, loading, error } = useFetch(
    fpId ? `${API_BASE}/manager/team-attainment?fiscal_period_id=${fpId}` : null
  );

  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  const positions = data?.positions ?? [];

  function handleRowClick(p) {
    if (selectedPosition?.position_id === p.position_id) {
      onSelectPosition(null);
    } else {
      onSelectPosition({ payout_summary_id: p.payout_summary_id, position_id: p.position_id, full_name: p.full_name, calculation_mode: p.calculation_mode });
    }
  }

  function toggleNode(positionId) {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) { next.delete(positionId); } else { next.add(positionId); }
      return next;
    });
  }

  function getHiddenIds(positions, collapsed) {
    const hidden = new Set();
    let suppressFromDepth = null;
    for (const p of positions) {
      if (suppressFromDepth !== null) {
        if (p.depth > suppressFromDepth) { hidden.add(p.position_id); continue; }
        else { suppressFromDepth = null; }
      }
      if (collapsed.has(p.position_id) && p.has_direct_reports) {
        suppressFromDepth = p.depth;
      }
    }
    return hidden;
  }

  const hiddenIds = getHiddenIds(positions, collapsedNodes);
  const visiblePositions = positions.filter((p) => !hiddenIds.has(p.position_id));

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <Card title="Team Attainment">
        {!fpId && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No open fiscal period found.</p>}
        {fpId && loading && <LoadingRow />}
        {fpId && error && <ErrorNote message="Unable to load team attainment data." />}
        {data && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Total Positions" value={data.total_positions ?? "—"} />
            <Field label="Total Payout"    value={fmt(data.total_payout)} />
            <Field label="Avg Attainment"  value={fmtPct(data.average_attainment_pct)} />
            <Field label="Above 100%"      value={data.positions_above_100pct ?? "—"} />
            <Field label="At Gate"         value={data.positions_at_gate ?? "—"} />
            {data.team_quota  != null && <Field label="Team Quota"  value={fmt(data.team_quota)} />}
            {data.team_credit != null && <Field label="Team Credit" value={fmt(data.team_credit)} />}
          </div>
        )}
      </Card>

      {/* Attainment distribution bars */}
      {data && data.total_positions > 0 && (
        <Card title="Attainment Distribution">
          <div className="space-y-3">
            <div>
              <div className="mb-1 flex items-center justify-between text-tok-xs text-[rgb(var(--color-text-muted))]">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[rgb(var(--color-success))]" />Above 100%</span>
                <span>{data.positions_above_100pct} / {data.total_positions}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[rgb(var(--color-surface-muted))]">
                <div className="h-full rounded-full bg-[rgb(var(--color-success))] transition-all" style={{ width: `${(data.positions_above_100pct / data.total_positions) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-tok-xs text-[rgb(var(--color-text-muted))]">
                <span className="flex items-center gap-1.5"><TrendingUp className="h-3 w-3 text-[rgb(var(--color-warning))]" />ARR Gate Met</span>
                <span>{data.positions_at_gate} / {data.total_positions}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[rgb(var(--color-surface-muted))]">
                <div className="h-full rounded-full bg-[rgb(var(--color-warning))] transition-all" style={{ width: `${(data.positions_at_gate / data.total_positions) * 100}%` }} />
              </div>
            </div>
            {data.positions_above_100pct < data.total_positions && (
              <div className="mt-2 flex items-start gap-2 rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{data.total_positions - data.positions_above_100pct} position{data.total_positions - data.positions_above_100pct !== 1 ? "s" : ""} below 100% attainment this period.</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Hierarchical position table — click row for PayoutDetailDrawer */}
      {positions.length > 0 && (
        <Card title="Position Breakdown">
          <div className="mt-2 max-h-[520px] overflow-y-auto space-y-1">
            <div className="grid grid-cols-4 gap-2 px-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              <span>Name</span>
              <span className="text-right">Quota</span>
              <span className="text-right">Credit</span>
              <span className="text-right">Attainment</span>
            </div>
            {visiblePositions.map((p) => {
              const isSelected  = selectedPosition?.payout_summary_id === p.payout_summary_id;
              const isCollapsed = collapsedNodes.has(p.position_id);
              const indentClass = DEPTH_INDENT[p.depth] ?? "pl-12";
              const modeTag     = calcModeLabel(p.calculation_mode);
              const isNode      = p.has_direct_reports;
              return (
                <div key={p.position_id} className={indentClass}>
                  <div className={`rounded-xl border transition ${isSelected ? "border-[rgb(var(--color-border-strong))] bg-[rgb(var(--color-surface-muted))]" : "border-[rgb(var(--color-border))]"}`}>
                    <div className="flex items-center gap-1">
                      {isNode ? (
                        <button onClick={() => toggleNode(p.position_id)} className="shrink-0 pl-2 py-2.5 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]" aria-label={isCollapsed ? "Expand" : "Collapse"}>
                          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                        </button>
                      ) : (
                        <div className="w-6 shrink-0" />
                      )}
                      <button onClick={() => handleRowClick(p)} className="flex-1 grid grid-cols-4 gap-2 py-2.5 pr-3 text-tok-sm text-left" aria-expanded={isSelected}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className={`font-medium truncate ${isNode ? "text-[rgb(var(--color-text))]" : "text-[rgb(var(--color-text))]"}`}>{p.full_name}</span>
                          {modeTag && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-tok-xs font-medium ${modeTag.cls}`}>{modeTag.label}</span>}
                        </span>
                        <span className="text-right text-[rgb(var(--color-text-muted))]">{fmt(p.quota_reporting)}</span>
                        <span className="text-right text-[rgb(var(--color-text-muted))]">{fmt(p.credit_total_reporting)}</span>
                        <span className="text-right">
                          <Pill tone={Number(p.attainment_pct) >= 1 ? "Complete" : Number(p.attainment_pct) >= 0.8 ? "Ready" : "Draft"}>
                            {fmtPct(p.attainment_pct)}
                          </Pill>
                        </span>
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel: TeamStatements
// GET /api/v1/statement-batches?fiscal_period_id={id} — released only,
//   server-enforced for manager. S162: fiscal_period_id wired from period prop.
// GET /api/v1/statement-batches/{id}/statements — §15.6 drill-down (S162).
// ---------------------------------------------------------------------------

// StatementOutputDrawer — §15.6 drill-down, now with tree hierarchy
// Fetches statement rows then matches to team-payout positions for depth/mode info.
function StatementOutputDrawer({ batchId, period, onClose, selectedPosition, onSelectPosition }) {
  const { data, loading, error } = useFetch(
    batchId ? `${API_BASE}/statement-batches/${batchId}/statements` : null
  );
  // Fetch team-payout for the same period to get hierarchy metadata
  const fpId = period?.fiscal_period_id;
  const { data: teamData } = useFetch(
    fpId ? `${API_BASE}/manager/team-payout?fiscal_period_id=${fpId}` : null
  );

  const rows = data?.data ?? [];
  const teamPositions = teamData?.positions ?? [];

  // Build lookup: position_id → { depth, has_direct_reports, calculation_mode }
  const positionMeta = {};
  for (const p of teamPositions) {
    positionMeta[p.position_id] = p;
  }

  // Sort rows in tree order using teamPositions order
  const posOrder = {};
  teamPositions.forEach((p, i) => { posOrder[p.position_id] = i; });
  const sortedRows = [...rows].sort((a, b) => {
    const ai = posOrder[a.position_id] ?? 999;
    const bi = posOrder[b.position_id] ?? 999;
    return ai - bi;
  });

  // Collapse state
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  function toggleNode(positionId) {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) { next.delete(positionId); } else { next.add(positionId); }
      return next;
    });
  }
  function getHiddenIds(rows, collapsed) {
    const hidden = new Set();
    let suppressFromDepth = null;
    for (const r of rows) {
      const meta = positionMeta[r.position_id];
      const depth = meta?.depth ?? 1;
      if (suppressFromDepth !== null) {
        if (depth > suppressFromDepth) { hidden.add(r.position_id); continue; }
        else { suppressFromDepth = null; }
      }
      if (collapsed.has(r.position_id) && meta?.has_direct_reports) {
        suppressFromDepth = depth;
      }
    }
    return hidden;
  }
  const hiddenIds = getHiddenIds(sortedRows, collapsedNodes);
  const visibleRows = sortedRows.filter((r) => !hiddenIds.has(r.position_id));

  return (
    <div className="mt-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-2.5">
        <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Statement Detail</p>
        <button onClick={onClose} className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-3 space-y-1 max-h-[480px] overflow-y-auto">
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load statement detail. Please try again." />}
        {!loading && !error && rows.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No statement rows found.</p>}
        {visibleRows.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-2 px-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-1">
              <span>Name</span>
              <span className="text-right">Payout</span>
              <span className="text-right">Currency</span>
            </div>
            {visibleRows.map((r) => {
              const meta = positionMeta[r.position_id];
              const depth = meta?.depth ?? 1;
              const isNode = meta?.has_direct_reports ?? false;
              const isSelected = selectedPosition?.position_id === r.position_id;
              const isCollapsed = collapsedNodes.has(r.position_id);
              const modeTag = calcModeLabel(meta?.calculation_mode);
              const indentClass = DEPTH_INDENT[depth] ?? "pl-12";
              return (
                <div key={r.statement_output_id} className={indentClass}>
                  <div className={`flex items-center gap-1 rounded-xl border ${isSelected ? "border-[rgb(var(--color-border-strong))] bg-[rgb(var(--color-surface-muted))]" : "border-[rgb(var(--color-border))]"}`}>
                    {isNode ? (
                      <button onClick={(e) => { e.stopPropagation(); toggleNode(r.position_id); }} className="shrink-0 pl-2 py-2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]" aria-label={isCollapsed ? "Expand" : "Collapse"}>
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                      </button>
                    ) : (
                      <div className="w-6 shrink-0" />
                    )}
                    <button className="flex-1 grid grid-cols-3 gap-2 py-2 pr-3 text-tok-sm text-left" onClick={() => { if (selectedPosition?.position_id === r.position_id) { onSelectPosition(null); } else { onSelectPosition({ payout_summary_id: meta?.payout_summary_id ?? null, position_id: r.position_id, full_name: r.full_name ?? "—", calculation_mode: meta?.calculation_mode }); } }}>
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className={`font-medium truncate ${isNode ? "text-[rgb(var(--color-text))]" : "text-[rgb(var(--color-text))]"}`}>
                          {r.full_name ?? "—"}
                        </span>
                        {modeTag && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-tok-xs font-medium ${modeTag.cls}`}>{modeTag.label}</span>}
                      </span>
                      <span className="text-right text-[rgb(var(--color-text))]">
                        {r.total_payout_amount != null ? Number(r.total_payout_amount).toLocaleString("en-US", { style: "currency", currency: r.payout_currency_code ?? "USD" }) : "—"}
                      </span>
                      <span className="text-right text-[rgb(var(--color-text-muted))]">{r.payout_currency_code ?? "—"}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function TeamStatements({ period, selectedPosition, onSelectPosition }) {
  const fpId = period?.fiscal_period_id;
  const { data, loading, error } = useFetch(
    fpId ? `${API_BASE}/statement-batches?fiscal_period_id=${fpId}` : `${API_BASE}/statement-batches`
  );
  const batches = data?.data ?? [];
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const fmt2 = (val) => val != null ? Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—";

  return (
    <div className="space-y-4">
      <Card title="Team Statements">
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load statement data." />}
        {!loading && !error && batches.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No released statements available for your team this period.</p>
        )}
        {batches.length > 0 && (
          <div className="mt-2 space-y-2">
            {batches.map((b) => {
              const isSelected = selectedBatchId === b.statement_batch_id;
              return (
                <div key={b.statement_batch_id}>
                  <button
                    onClick={() => setSelectedBatchId((prev) => (prev === b.statement_batch_id ? null : b.statement_batch_id))}
                    className={`w-full rounded-xl border px-3 py-2.5 text-left transition ${isSelected ? "border-[rgb(var(--color-border-strong))] bg-[rgb(var(--color-surface-muted))]" : "border-[rgb(var(--color-border))] hover:border-[rgb(var(--color-border-strong))] hover:bg-[rgb(var(--color-surface-muted))]"}`}
                    aria-expanded={isSelected}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))] transition-transform ${isSelected ? "rotate-90" : ""}`} />
                        <div className="min-w-0">
                          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))] truncate">
                            {b.batch_label ?? `Batch ${String(b.statement_batch_id).slice(0, 8)}`}
                          </p>
                          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                            {b.period_label ?? b.period_code ?? (b.fiscal_period_id ? `Period ${b.fiscal_period_id}` : "—")}
                            {b.released_at ? ` · Released ${new Date(b.released_at).toLocaleDateString()}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {b.total_payout_amount != null && <span className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{fmt2(b.total_payout_amount)}</span>}
                        <Pill tone="Complete">Released</Pill>
                      </div>
                    </div>
                  </button>
                  {isSelected && (
                    <StatementOutputDrawer
                      batchId={b.statement_batch_id}
                      period={period}
                      onClose={() => setSelectedBatchId(null)}
                      selectedPosition={selectedPosition}
                      onSelectPosition={onSelectPosition}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ManagerCopilot removed S241 — replaced by shared CopilotPanel.

// ---------------------------------------------------------------------------
// Manager ContextHeader (spec §6.4) — with period selector (OI-PERIOD-SCOPING)
// ---------------------------------------------------------------------------

function ManagerContextHeader({ pageTitle, periods, selectedPeriod, onPeriodChange, userName }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
          {pageTitle}
        </h1>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {userName && <span>{userName}</span>}
        </div>
      </div>
      {periods && periods.length > 0 && (
        <select
          value={selectedPeriod?.fiscal_period_id ?? ""}
          onChange={(e) => {
            const p = periods.find((x) => String(x.fiscal_period_id) === e.target.value);
            if (p) onPeriodChange(p);
          }}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
        >
          {periods.slice().reverse().map((p) => (
            <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
              {p.period_label ?? p.period_code}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manager Left Nav (spec §3.3)
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { to: "/manager/my-payout",   label: "My Payout",       icon: DollarSign },
  { to: "/manager/team-payout", label: "Team Payout",     icon: Users      },
  { to: "/manager/statements",  label: "Team Statements", icon: FileText   },
];

const PAGE_TITLES = {
  "/manager/my-payout":   "My Payout",
  "/manager/team-payout": "Team Payout",
  "/manager/statements":  "Team Statements",
};

function ManagerNav({ mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-52 overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <nav className="p-3 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                  isActive
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// ManagerShell root
// ---------------------------------------------------------------------------

export default function ManagerShell() {
  const { user } = useAuth();
  const location = useLocation();

  // Clear side panel when navigating to a different route
  useEffect(() => {
    setSelectedPosition(null);
  }, [location.pathname]);

  // M040 C0: dark/setDark state and useEffect removed — ThemeButton is self-contained.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  // Period selector — fetch all periods, slice to 5 most recent (OI-PERIOD-SCOPING)
  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = (periodsData?.data ?? [])  // S245 fix: envelope key is 'data' not 'fiscal_periods'
    .slice()
    .sort((a, b) => new Date(b.period_start_date) - new Date(a.period_start_date))
    .slice(0, 13);
    // S245: 13 periods = 12 months (full fiscal year) + 1 for YoY comparison.
    // Batch-driven default lands on the period with actual data.

  // S245: hoist batch fetch to shell root so we can default selectedPeriod to the
  // period of the most recent released batch (mirrors RepShell pattern).
  // TeamStatements still fetches its own batches scoped to selectedPeriod.
  const { data: batchesData, loading: batchesLoading } = useFetch(`${API_BASE}/statement-batches`);
  const rootBatches = batchesData?.data ?? [];

  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  useEffect(() => {
    if (selectedPeriod !== null || periods.length === 0) return;
    if (batchesLoading) return;
    // Default to the period of the most recent released batch — same logic as RepShell.
    // Falls back to periods[0] (most recent period) if no batch exists.
    const batchPeriodId = rootBatches?.[0]?.fiscal_period_id ?? null;
    const defaultPeriod = batchPeriodId
      ? (periods.find((p) => p.fiscal_period_id === batchPeriodId) ?? periods[0])
      : periods[0];
    setSelectedPeriod(defaultPeriod);
  }, [periods, rootBatches, batchesLoading]);

  // Profile fetch — nav gate source per DR-004 (S163)
  const profileUrl = selectedPeriod
    ? `${API_BASE}/manager/profile?fiscal_period_id=${selectedPeriod.fiscal_period_id}`
    : null;
  const { data: profileData } = useFetch(profileUrl);
  const profile = profileData ?? null;

  // S77 — tenant-context: proactive tier gate (Stage 10). ER-1 compliant.
  const { data: tenantContext } = useFetch(`${API_BASE}/tenant-context`);
  const aiQueryEnabled = tenantContext?.features?.allows_ai_query ?? null;

  const userName  = user?.full_name ?? user?.email ?? null;
  const pageTitle = PAGE_TITLES[location.pathname] ?? "Team Payout";

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={userName}
        userRole={user?.user_type ?? "manager"}
        userRoleLabel="Manager"
        mode={import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod"}
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen((v) => !v)}
      />

      <div className="flex min-h-[calc(100vh-64px)]">
        <ManagerNav mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        <main className={`min-w-0 flex-1 p-4 lg:p-6 transition-all duration-300 ${selectedPosition ? "pr-[340px]" : ""}`}>
          <ManagerContextHeader
            pageTitle={pageTitle}
            periods={periods}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            userName={userName}
          />

          <Routes>
            <Route index                element={<Navigate to="my-payout" replace />} />
            <Route path="my-payout"     element={<MyPayout period={selectedPeriod} profile={profile} />} />
            <Route path="team-payout"   element={<TeamPayout period={selectedPeriod} selectedPosition={selectedPosition} onSelectPosition={setSelectedPosition} />} />
            <Route path="statements"    element={<TeamStatements period={selectedPeriod} selectedPosition={selectedPosition} onSelectPosition={setSelectedPosition} />} />
            {/* Deep-link redirects — old routes preserved so existing bookmarks don't 404 */}
            <Route path="my-attainment" element={<Navigate to="/manager/my-payout" replace />} />
            <Route path="attainment"    element={<Navigate to="/manager/team-payout" replace />} />
            <Route path="*"             element={<Navigate to="my-payout" replace />} />
          </Routes>

          {/* Detail side panel — shared across Team Payout, Attainment, Statements */}
          <DetailSidePanel
            selection={selectedPosition}
            fiscalPeriodId={selectedPeriod?.fiscal_period_id}
            onClose={() => setSelectedPosition(null)}
          />

        </main>

        {/* Copilot — shared CopilotPanel, S241 wiring. Sibling to main, not child. */}
        <CopilotPanel
          open={copilotOpen}
          fiscalPeriodId={selectedPeriod?.fiscal_period_id ?? null}
          stageId={null}
          stageName={null}
          aiQueryEnabled={aiQueryEnabled}
        />
      </div>
    </div>
  );
}
